import { Injectable } from '@angular/core';
import { FigmaColor, FigmaEffect, FigmaNode, TreeNode } from './types';

/**
 * Defines the rules of the high-level tokens that must
 * be provided in the two Figma nodes (Foundation and Colors).
 *
 * These tokens must reflect the values used in the Figma designs
 * and must striclty follow the naming provided below. The naming
 * of the inner tokens does not need to change.
 */
const TOKEN_REGISTRY = {
  foundation: {
    spacing: {
      required: true,
      regex: /^\d+(\.\d+)?px$/,
    },
    'border-radius': {
      required: true,
      regex: /^\d+(\.\d+)?px$/,
    },
    'border-width': {
      required: true,
      regex: /^\d+(\.\d+)?px$/,
    },
    'line-height': {
      required: true,
      regex: /^\d+(\.\d+)?px$/,
    },
    'letter-spacing': {
      required: false,
      regex: /^\d+(\.\d+)?px$/,
    },
    'font-size': {
      required: true,
      regex: /^\d+(\.\d+)?px$/,
    },
    'font-weight': {
      required: true,
      regex: /^[1-9]00$/,
    },
    'font-family': {
      required: 'true',
    },
    padding: {
      required: false,
      regex: /^\d+(\.\d+)?px$/,
    },
    margin: {
      required: false,
      regex: /^\d+(\.\d+)?px$/,
    },
    gap: {
      required: false,
      regex: /^\d+(\.\d+)?px$/,
    },
  },
  colors: {
    regex:
      /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$|^rgba\(\s*(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\s*,\s*(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\s*,\s*(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\s*,\s*(1|0|0?\.\d+)\s*\)$/,
  },
};

/**
 * Maps Figma keys to keys of the `token.config.json`.
 */
const FIGMA_MAPPER = {
  fontSize: ['font-size'],
  fontWeight: ['font-weight'],
  letterSpacing: ['letter-spacing'],
  lineHeightPx: ['line-height'],
  backgroundColor: ['colors'],
  paddingLeft: ['padding', 'spacing'],
  paddingRight: ['padding', 'spacing'],
  paddingTop: ['padding', 'spacing'],
  paddingBottom: ['padding', 'spacing'],
  borderRadius: ['border-radius'],
  borderColor: ['colors'],
  borderWidth: ['border-width'],
  itemSpacing: ['gap', 'spacing'],
  fill: ['colors'],
  stroke: ['colors'],
  color: ['colors'],
};

/**
 * Defines token units for each category.
 */
const TOKEN_UNITS = {
  'font-size': 'px',
  'font-weight': null,
  'letter-spacing': 'px',
  'line-height': 'px',
  colors: 'hex',
  padding: 'px',
  spacing: 'px',
  'border-radius': 'px',
  'border-width': 'px',
  gap: 'px',
};

type TokenObject = { [key: string]: string | TokenObject };
export type TokenConfiguration = Record<string, TokenObject>;

export type Errors = {
  structure: Set<string>;
  missing: Set<string>;
  invalid: Set<string>;
  groups: Set<string>;
};

export type ErrorsArray = {
  structure: string[];
  missing: string[];
  invalid: string[];
  groups: string[];
};

export type TokenCheck = {
  errorMessages: string[] | null;
  errors: ErrorsArray | null;
  tokenConfiguration: TokenConfiguration | null;
};

@Injectable({
  providedIn: 'root',
})
export class UtilitiesService {
  /**
   * Performs a full pre-validation routine on foundation and color token nodes.
   *
   * This function:
   * 1. Validates the structure of the `foundationNode` and `colorsNode` using `checkFigmaNode`.
   * 2. Extracts and normalizes token lines from both nodes.
   * 3. Initializes an `errors` object to track structural issues, invalid values, missing tokens, and ungrouped tokens.
   * 4. Checks the naming structure and group conformance of all tokens.
   * 5. Validates foundation and color tokens against the rules defined in `TOKEN_REGISTRY`.
   * 6. Logs detected issues to the console.
   *
   * The process halts early if the node structure is invalid or if structural token naming errors are found.
   *
   * @param {Object} foundationNode - The node object containing raw foundation token definitions (e.g., from Figma).
   * @param {Object} colorsNode - The node object containing raw color token definitions.
   * @returns {TokenCheck} An object containing the potential errors and the token configuration.
   */
  public preCheckTokens(foundationNode: any, colorsNode: any): TokenCheck {
    const errors: Errors = {
      structure: new Set(),
      missing: new Set(),
      invalid: new Set(),
      groups: new Set(),
    };

    const errorMessages = [];
    // Checking the foundation node making sure it follows the
    // overall structure.
    const { error: foundationError, characters: foundationCharacters } =
      this.checkFigmaNode(foundationNode);
    if (foundationError) {
      errorMessages.push(`Foundation Node: ${foundationError}`);
    }

    // Checking the colors node making sure it follows the
    // overall structure.
    const { error: colorsError, characters: colorsCharacters } =
      this.checkFigmaNode(colorsNode);
    if (colorsError) {
      errorMessages.push(`Colors' Node: ${colorsError}`);
    }

    if (errorMessages.length > 0) {
      return {
        errorMessages,
        errors: this.setToArray(errors),
        tokenConfiguration: null,
      };
    }

    const foundationTokens = foundationCharacters
      .split('\n')
      .filter((token: string) => token.trim() !== '')
      .map((token: string) => token.trim().toLowerCase())
      .sort();

    const colorTokens = colorsCharacters
      .split('\n')
      .filter((token: string) => token.trim() !== '')
      .map((token: string) => token.trim().toLowerCase())
      .sort();

    // Making sure both foundation and color tokens are structured properly
    this.checkNameStructureAndGroups(
      foundationTokens.concat(colorTokens),
      errors
    );

    if (errors.structure.size > 0) {
      // Structuring errors
      return {
        errorMessages: null,
        errors: this.setToArray(errors),
        tokenConfiguration: null,
      };
    }

    // Making sure both foundation and color tokens are valid
    const foundationTokensConfig = this.checkFoundationValidity(
      foundationTokens,
      errors
    );
    const colorTokensConfig = this.checkColorsValidity(colorTokens, errors);

    // If there are no errors, we construct the `token.config.json` file.
    if (
      errors.invalid.size === 0 &&
      errors.missing.size === 0 &&
      errors.invalid.size === 0
    ) {
      return {
        errorMessages: null,
        errors: this.setToArray(errors),
        tokenConfiguration: {
          ...foundationTokensConfig,
          colors: { ...colorTokensConfig },
        },
      };
    }

    return {
      errorMessages: null,
      errors: this.setToArray(errors),
      tokenConfiguration: null,
    };
  }

  /**
   * Converts all `Set` properties of the given `Errors` object into arrays.
   *
   * @param {Errors} errors - The object containing the error sets to convert.
   * @returns {ErrorsArray} A new object with the same properties, where each `Set` is converted to an array.
   */
  private setToArray(error: Errors): ErrorsArray {
    return {
      structure: Array.from(error['structure']),
      invalid: Array.from(error['invalid']),
      missing: Array.from(error['missing']),
      groups: Array.from(error['groups']),
    };
  }

  /**
   * Converts a 3-digit or 6-digit hexadecimal color code to an rgba string with full opacity.
   *
   * @param {string} hex - A valid hex color string starting with "#" (e.g. "#abc" or "#aabbcc").
   * @returns {string} The corresponding rgba string in the format "rgba(r, g, b, 1)".
   */
  private hexToRgba(hex: string): string {
    let normalizedHex = hex.slice(1);
    if (normalizedHex.length === 3) {
      normalizedHex = normalizedHex
        .split('')
        .map((char) => char + char)
        .join('');
    }

    const r = parseInt(normalizedHex.slice(0, 2), 16);
    const g = parseInt(normalizedHex.slice(2, 4), 16);
    const b = parseInt(normalizedHex.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, 1)`;
  }

  /**
   * Checks whether a string contains exactly one colon (`:`) character.
   *
   * @param {string} token - The input string to check.
   * @returns {boolean} `true` if the string contains exactly one colon, otherwise `false`.
   */
  private hasExactlyOneColon(token: string) {
    return (token.match(/:/g) || []).length === 1;
  }

  /**
   * Validates whether a string is composed of alphanumeric words separated by hyphens.
   *
   * The string must:
   * - Start and end with an alphanumeric character.
   * - Contain zero or more hyphen-separated segments.
   * - Not contain any spaces or special characters.
   *
   * @param {string} token - The input string to validate.
   * @returns {boolean} `true` if the string follows the hyphen-separated format, otherwise `false`.
   */

  private isHyphenSeparatedWords(token: string): boolean {
    return /^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*$/.test(token);
  }

  /**
   * Validates the structure of a Figma nodes containing tokens to ensure it contains exactly one node
   * with a single child node that includes token characters.
   *
   * @param {Object} data - The data object containing nodes with token definitions (directly from Figma).
   *
   * @returns {{ error: string | null, characters: string }} An object containing:
   * - `error`: a descriptive error message if validation fails, otherwise `null`.
   * - `characters`: the token character string if valid, otherwise `''`.
   */
  private checkFigmaNode(data: any): {
    error: string | null;
    characters: string;
  } {
    if (data.nodes == null || Object.keys(data.nodes).length !== 1) {
      return {
        error: 'There must be exactly one node defining the tokens',
        characters: '',
      };
    }

    const node = Object.keys(data.nodes)[0];

    if (
      data.nodes[node].document.children == null ||
      data.nodes[node].document.children.length !== 1
    ) {
      return {
        error:
          'There must be exactly one child node defining the tokens inside the parent node',
        characters: '',
      };
    }

    const characters = data.nodes[node].document.children[0].characters;
    if (characters == null || characters.trim() === '') {
      return { error: 'No tokens found in the token node', characters: '' };
    }

    return { error: null, characters: characters };
  }

  /**
   * Validates the structure and values of foundation tokens against a token registry,
   * and constructs a `tokens` object for use in Tailwind configuration.
   *
   * This function checks if the CSS variables match keys in the `TOKEN_REGISTRY.foundation`,
   * and verifies that their values conform to the regex pattern (if provided). It also checks for
   * missing required tokens in the foundation registry.
   *
   * @param {string[]} foundationTokens - An array of foundation token strings, each in the format `name: value`.
   * @param {Errors} errors - An object used to collect validation errors.
   *
   * @returns {TokenConfiguration} tokens - An object containing the tokens in a format close to `tailwind.config.js` format.
   */
  private checkFoundationValidity(
    foundationTokens: string[],
    errors: Errors
  ): TokenConfiguration {
    const found: {
      [category: string]: boolean;
    } = {};
    const tokens: TokenConfiguration = {};
    // Making sure the tokens are part of the supported
    // keys in the `TOKEN_REGISTRY`
    for (const variable of foundationTokens) {
      const variableNameParts = variable.split(':')[0].split('-');
      const category = variableNameParts
        .slice(0, variableNameParts.length - 1)
        .join('-');
      const level = variableNameParts[variableNameParts.length - 1];

      if ((TOKEN_REGISTRY as any).foundation[category] != null) {
        if (found[category] == null) {
          found[category] = true;
          tokens[category] = {};
        }

        const value = variable.split(':')[1].trim();
        // Enforcing the format of the token value if there is a certain format
        // defined in the `TOKEN_REGISTRY`.
        if ((TOKEN_REGISTRY as any).foundation[category].regex != null) {
          const valid = (TOKEN_REGISTRY as any).foundation[category].regex.test(
            value
          );
          if (!valid) {
            errors.invalid.add(variable);
          } else {
            tokens[category][level] = value;
          }
        } else {
          tokens[category][level] = value;
        }
      } else {
        errors.invalid.add(variable);
      }
    }

    // Making sure all required tokens are present in the Foundation and Colors
    // nodes.
    for (const token of Object.keys(TOKEN_REGISTRY.foundation)) {
      if ((TOKEN_REGISTRY as any).foundation[token].required && !found[token]) {
        errors.missing.add(token);
      }
    }

    return tokens;
  }

  /**
   * Validates the structure of token names and groups them based on shared prefixes.
   *
   * This function ensures each token string:
   * - Contains exactly one colon separating name and value.
   * - Has non-empty name and value parts.
   * - Uses a hyphen-separated naming convention with at least one hyphen.
   *
   * Tokens are grouped by their shared base name (i.e., all but the last hyphen-separated segment).
   * If a token is not part of a group (i.e., it's the only one in its group), it's reported as a potential
   * error in the `errors.groups` object.
   *
   * @param {string[]} foundationTokens - Array of foundation token strings in the format `token-name-{scale}: value`.
   * @param {Errors} errors - An object used to collect structure and grouping errors or warnings.
   */
  private checkNameStructureAndGroups(
    foundationTokens: string[],
    errors: Errors
  ) {
    const groups: {
      [category: string]: string[];
    } = {};

    for (const variable of foundationTokens) {
      if (!this.hasExactlyOneColon(variable)) {
        errors.structure.add(variable);
      }

      const parts = variable.split(':');
      if (parts.length !== 2) {
        errors.structure.add(variable);
      } else {
        if (parts[0].trim().length === 0 || parts[1].trim().length === 0) {
          errors.structure.add(variable);
        } else {
          if (
            !this.isHyphenSeparatedWords(parts[0].trim()) ||
            parts[0].trim().split('-').length < 2
          ) {
            errors.structure.add(variable);
          } else {
            const subtokens = parts[0].trim().split('-');
            const base = subtokens.slice(0, subtokens.length - 1).join('-');
            if (groups[base] != null) {
              groups[base].push(parts[0].trim());
            } else {
              groups[base] = [parts[0].trim()];
            }
          }
        }
      }
    }

    for (const property of Object.keys(groups)) {
      if (groups[property].length === 1) {
        errors.groups.add(groups[property][0]);
      }
    }
  }

  /**
   * Validates the format of color tokens against a regex pattern from the token registry
   * and constructs a structured `tokens` object compatible with Tailwind's color configuration.
   *
   * This function checks whether each color value list matches the expected
   * format defined in `TOKEN_REGISTRY.colors.regex`. Invalid color tokens are recorded in the
   * `errors.invalid` object.
   *
   * @param {string[]} colorTokens - An array of color tokens strings in the format `name: value`.
   * @param {Errors} errors - An object used to track validation errors.
   *
   * @returns {TokenConfiguration} tokens - An object containing the tokens in a format close to `tailwind.config.js` format.
   */
  private checkColorsValidity(
    colorTokens: string[],
    errors: Errors
  ): TokenConfiguration {
    const tokens: TokenConfiguration = {};
    // Making sure all color values have the appropriate format.
    for (const variable of colorTokens) {
      const variableNameParts = variable.split(':')[0].split('-');
      const value = variable.split(':')[1].trim();
      const valid = TOKEN_REGISTRY.colors.regex.test(value);

      if (!valid) {
        errors.invalid.add(variable.split(':')[0].trim());
        // errors.invalid[variable.split(':')[0].trim()] = true;
      } else {
        const category = variableNameParts
          .slice(0, variableNameParts.length - 1)
          .join('-');
        const level = variableNameParts[variableNameParts.length - 1];

        if (tokens[category] == null) {
          tokens[category] = {};
        }

        tokens[category][level] = value;
      }
    }

    return tokens;
  }

  // REUSED FROM VSCODE EXTENSION
  public getComponents(node: FigmaNode): FigmaNode[] {
    if (node.type === 'COMPONENT_SET' && node.children) {
      return node.children;
    }

    if (node.type === 'COMPONENT' || node.type === 'FRAME') {
      return [node];
    }

    throw new Error(`Unsupported node type: ${node.type}`);
  }

  // REUSED FROM VSCODE EXTENSION
  private rgbaToHex(rgba: FigmaColor): string {
    const r = Math.round(rgba.r * 255);
    const g = Math.round(rgba.g * 255);
    const b = Math.round(rgba.b * 255);

    return `#${((1 << 24) | (r << 16) | (g << 8) | b)
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  }

  // REUSED FROM VSCODE EXTENSION
  private figmaShadowToCSS(shadow: FigmaEffect): string | null {
    if (!shadow || shadow.type !== 'DROP_SHADOW') {
      return null;
    }

    const { r, g, b, a } = shadow.color!;
    const { x, y } = shadow.offset!;
    const radius = shadow.radius;
    const spread = shadow.spread;

    // Convert RGB from 0-1 range to 0-255
    const red = Math.round(r * 255);
    const green = Math.round(g * 255);
    const blue = Math.round(b * 255);

    return `${x}px ${y}px ${radius}px ${spread}px rgba(${red}, ${green}, ${blue}, ${a})`;
  }

  // REUSED FROM VSCODE EXTENSION
  public parseTreeNode(tree: FigmaNode[]): TreeNode[] {
    let stack: {
      original: any;
      parent: any | null;
    }[] = tree.map((node) => ({ original: node, parent: null }));
    let newTree: any[] = [];

    while (stack.length > 0) {
      let popped = stack.pop();
      if (!popped || !popped.original) {
        continue;
      }
      let { original, parent } = popped;

      if (!original.absoluteBoundingBox) {
        throw new Error('No absolute bounding box found');
      }

      let newNode: any = {
        id: original.id,
        name: original.name,
        type: original.type,
        styles: {
          ...original.style,
          backgroundColor: original.backgroundColor
            ? this.rgbaToHex(original.backgroundColor)
            : null,
          paddingLeft: original.paddingLeft,
          paddingRight: original.paddingLeft,
          paddingTop: original.paddingTop,
          paddingBottom: original.paddingBottom,
          borderRadius: original.cornerRadius,
          borderWidth: original.strokeWeight,
          borderColor: original.strokes?.[0]?.color
            ? this.rgbaToHex(original.strokes[0].color)
            : null,
          wrap: original.layoutWrap,
          verticalAxisAlignment: original.counterAxisAlignItems,
          horizontalAxisAlignment: original.primaryAxisAlignItems,
          height: original.absoluteBoundingBox.height,
          width: original.absoluteBoundingBox.width,
          itemSpacing: original.itemSpacing,
          fill: original.fills?.[0]?.color
            ? this.rgbaToHex(original.fills[0].color)
            : null,
          stroke: original.strokes?.[0]?.color
            ? this.rgbaToHex(original.strokes[0].color)
            : null,
          boxShadow: original.effects?.[0]
            ? this.figmaShadowToCSS(original.effects[0])
            : null,
          color: original.fills?.[0]?.color
            ? this.rgbaToHex(original.fills[0].color)
            : null,
        },
        children: [],
      };

      if (parent) {
        parent.children.push(newNode);
      } else {
        newTree.push(newNode);
      }

      if (original.children && original.children.length > 0) {
        for (let i = original.children.length - 1; i >= 0; i--) {
          stack.push({
            original: original.children[i],
            parent: newNode,
          });
        }
      }
    }

    return newTree.reverse();
  }

  /**
   * Validates Figma component styles against a given token configuration.
   *
   * Traverses a Figma document node tree and attempts to match each style
   * property to a known token defined in the `tokens` configuration.
   * If a style value doesn't match any token, an error message is recorded.
   *
   * Matching includes support for token categories like color (with `hex` values
   * converted to `rgba`) and units like `px`, using a predefined `FIGMA_MAPPER` and `TOKENS_UNITS`.
   *
   * @param {TreeNode[]} tree - The Figma document node tree (array of nodes) to validate.
   * @param {TokenConfiguration} tokens - The design token configuration, allowing nested levels.
   * @returns {string[]} An array of validation error messages describing unmatched styles.
   */
  public validateComponents(
    tree: TreeNode[],
    tokens: TokenConfiguration
  ): string[] {
    let stack = tree.map((node: TreeNode) => ({ original: node }));
    const errors = [];

    while (stack.length > 0) {
      let popped = stack.pop();
      if (!popped || !popped.original) {
        continue;
      }
      let { original } = popped;

      if (original.styles != null) {
        for (const prop of Object.keys(original.styles)) {
          if (
            (FIGMA_MAPPER as any)[prop] != null &&
            (original.styles as any)[prop] != null
          ) {
            // If the value is numerical and less than 1 (referring to pixels)
            // we IGNORE it.
            const num = Number((original.styles as any)[prop]);
            if (!isNaN(num) && num < 1) {
              continue;
            }

            for (const tokenCategory of (FIGMA_MAPPER as any)[prop]) {
              if (tokens[tokenCategory] != null) {
                let found = false;
                for (const level of Object.keys(tokens[tokenCategory])) {
                  let value = tokens[tokenCategory][level].toString();
                  if ((TOKEN_UNITS as any)[tokenCategory] === 'px') {
                    value = value.replace('px', '');
                  } else if ((TOKEN_UNITS as any)[tokenCategory] === 'hex') {
                    for (const colorLevel of Object.keys(
                      tokens[tokenCategory][level]
                    )) {
                      const isHex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
                      value = (tokens as any)[tokenCategory][level][colorLevel];
                      let figmaValue = (original.styles as any)[prop]
                        .toString()
                        .toLowerCase();
                      if (isHex.test(value)) {
                        value = this.hexToRgba(value);
                      }

                      if (isHex.test(figmaValue)) {
                        figmaValue = this.hexToRgba(figmaValue);
                      }

                      // Else, we assume it's in rgba format.
                      if (value === figmaValue) {
                        (original.styles as any)[
                          prop
                        ] = `${level}-${colorLevel}`;
                        found = true;
                        break;
                      }
                    }
                  }

                  if (
                    value ===
                    (original.styles as any)[prop].toString().toLowerCase()
                  ) {
                    (original.styles as any)[
                      prop
                    ] = `${tokenCategory}-${level}`;
                    found = true;
                    break;
                  }
                }

                if (!found) {
                  console.error(
                    `${prop}: ${JSON.stringify(
                      (original.styles as any)[prop]
                    )} does not exist in ${tokenCategory}`
                  );
                  errors.push(`Node ${original.id} has ${prop} set to 
                    ${JSON.stringify(
                      (original.styles as any)[prop]
                    )} but there is no token for it
                    `);
                }

                break;
              }
            }
          }
        }
      }

      if (original.children && original.children.length > 0) {
        for (let i = original.children.length - 1; i >= 0; i--) {
          stack.push({
            original: original.children[i],
          });
        }
      }
    }

    return errors;
  }
}
